import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JsonObject } from '../base/json';
import { PlainVersionedObject, VersionedObject, VersionedObjectConstructor, VersionedObjectHandlers } from '../base/versioned-object';
import { Rpc } from '../rpc/rpc';
import { RpcSettingsClient } from '../rpc/settings/rpc-settings-client';
import { RpcSettings, RpcSettingsOperationType, RpcSettingsProviderType, RpcSettingsScope } from '../rpc/settings/rpc-settings-model';
import { GatewayConnection } from './gateway-connection';

export const enum BannerHideType {
    /**
     * Indicates banner is not hidden (unusual)
     */
    None,
    /**
     * Indicates banner is hidden for the current session
     */
    Session,
    /**
     * Indicates banner is hidden permanently
     */
    Permanent
}

/**
 * Defines banner settings for the current user shared across the application
 */
export interface BannerUserSettings extends JsonObject {
    id: string;
    hideType: BannerHideType;
}

/**
 * The Versioned representation of the Shell User Settings
 * This object is used for settings that specific to the current user and to shell
 */
export class CommonUserSettings extends VersionedObject {

    private static propertyNames = {
        banners: 'banners'
    };

    /**
     * Getter for the latest version of the common user settings
     */
    public get latestVersion(): number {
        return 1;
    }

    /**
     * Getter for the users preference on banner settings
     */
    public get banners(): BannerUserSettings[] {
        return <BannerUserSettings[]>this.getProperty(CommonUserSettings.propertyNames.banners);
    }

    /**
     * Setter for the users preference on banner settings
     */
    public set banners(value: BannerUserSettings[]) {
        this.setProperty(CommonUserSettings.propertyNames.banners, value);
    }

    constructor(objectWrapper: PlainVersionedObject, handlers: VersionedObjectHandlers) {
        super(objectWrapper, handlers);
    }

    /**
     * Attempts to upgrade the current version of the object at least one version toward the latest version.
     * if this.currentVersion is null or undefined, then the upgrade should initialize to the latest version
     * this is called iteratively until the current version is equal to the latest version
     */
    protected upgrade() {
        // For now, we dont need to do anything but initialize to the latest version.
        // NOTE: When latestVersion is updated, then we need to add logic here to upgrade old user profile objects.
        // In this case, we ALWAYS must maintain backwards and forwards compatibility. This object is shared back and forth be
        if (MsftSme.isNullOrUndefined(this.currentVersion)) {
            this.clear();
            this.banners = [];
            this.currentVersion = this.latestVersion;
            return;
        } else if (this.currentVersion < this.latestVersion) {
            this.currentVersion = this.latestVersion;
        } else {
            // current version is newer than the version we know about. TODO: there may need to be handling here in the future.
        }
    }
}

/**
 * Defines the common application settings object that is shared through the whole application.
 */
export class CommonApplicationSettings extends VersionedObject {
    private static propertyNames = {
        // TODO: add properties when needed
    };

    /**
     * Getter for the latest version of the common application settings
     */
    public get latestVersion(): number {
        return 1;
    }

    constructor(objectWrapper: PlainVersionedObject, handlers: VersionedObjectHandlers) {
        super(objectWrapper, handlers);
    }

    /**
     * Attempts to upgrade the current version of the object at least one version toward the latest version.
     * if this.currentVersion is null or undefined, then the upgrade should initialize to the latest version
     * this is called iteratively until the current version is equal to the latest version
     */
    protected upgrade() {
        // For now, we dont need to do anything but initialize to the latest version.
        // NOTE: When latestVersion is updated, then we need to add logic here to upgrade old application settings objects.
        // In this case, we ALWAYS must maintain backwards and forwards compatibility. This object is shared back and forth be
        if (MsftSme.isNullOrUndefined(this.currentVersion)) {
            this.clear();
            this.currentVersion = this.latestVersion;
            return;
        } else if (this.currentVersion < this.latestVersion) {
            this.currentVersion = this.latestVersion;
        } else {
            // current version is newer than the version we know about. TODO: there may need to be handling here in the future.
        }
    }
}

/**
 * Defines the common admin settings object that is shared through the whole application.
 */
export class CommonAdminSettings extends VersionedObject {
    private static propertyNames = {
        connectivityLevel: 'connectivityLevel',
        autoGatewayUpdate: 'autoGatewayUpdate',
        autoExtensionsUpdate: 'autoExtensionsUpdate',
        scheduledUpdateTime: 'scheduledUpdateTime',
        updateRescheduled: 'updateRescheduled',
        sessionsExpirationMinutes: 'sessionsExpirationMinutes',
        credentialsExpirationTimeInMs: 'credentialsExpirationTimeInMs'
    };

    /**
     * Getter for the latest version of the common admin settings
     */
    public get latestVersion(): number {
        return 1;
    }

    /**
     * Getter for the admin's preference on the connectivity level
     */
    public get connectivityLevel(): string {
        return <string>this.getProperty(CommonAdminSettings.propertyNames.connectivityLevel);
    }

    /**
     * Setter for the admin's preference on the connectivity level
     */
    public set connectivityLevel(value: string) {
        this.setProperty(CommonAdminSettings.propertyNames.connectivityLevel, value);
    }

    public get autoGatewayUpdate(): boolean {
        return <boolean>this.getProperty(CommonAdminSettings.propertyNames.autoGatewayUpdate);
    }

    public set autoGatewayUpdate(value: boolean) {
        this.setProperty(CommonAdminSettings.propertyNames.autoGatewayUpdate, value);
    }

    public get autoExtensionsUpdate(): boolean {
        return <boolean>this.getProperty(CommonAdminSettings.propertyNames.autoExtensionsUpdate);
    }

    public set autoExtensionsUpdate(value: boolean) {
        this.setProperty(CommonAdminSettings.propertyNames.autoExtensionsUpdate, value);
    }

    public get scheduledUpdateTime(): string {
        return <string>this.getProperty(CommonAdminSettings.propertyNames.scheduledUpdateTime);
    }

    public set scheduledUpdateTime(value: string) {
        this.setProperty(CommonAdminSettings.propertyNames.scheduledUpdateTime, value);
    }

    public get updateRescheduled(): boolean {
        return <boolean>this.getProperty(CommonAdminSettings.propertyNames.updateRescheduled);
    }

    private sessionsExpirationMinutesCache: number;

    public get sessionsExpirationMinutes(): number {
        if (MsftSme.isNullOrUndefined(this.sessionsExpirationMinutesCache)) {
            this.sessionsExpirationMinutesCache = <number>this.getProperty(CommonAdminSettings.propertyNames.sessionsExpirationMinutes);
        }
        MsftSme.self().Init.sessionExpiration = this.sessionsExpirationMinutesCache;
        return this.sessionsExpirationMinutesCache;
    }

    public set sessionsExpirationMinutes(value: number) {
        this.setProperty(CommonAdminSettings.propertyNames.sessionsExpirationMinutes, value);
        this.sessionsExpirationMinutesCache = undefined;
        MsftSme.self().Init.sessionExpiration = undefined;
    }

    private credentialsExpirationTimeInMsCache: number;

    /**
     * Get stored manage as credentials expiration time in milliseconds
     */
    public get credentialsExpirationTimeInMs(): number {
        if (MsftSme.isNullOrUndefined(this.credentialsExpirationTimeInMsCache)) {
            this.credentialsExpirationTimeInMsCache = <number>this.getProperty(
                CommonAdminSettings.propertyNames.credentialsExpirationTimeInMs);
        }
        return this.credentialsExpirationTimeInMsCache;
    }

    /**
     * Store manage as credentials expiration time in milliseconds
     */
    public set credentialsExpirationTimeInMs(value: number) {
        this.setProperty(CommonAdminSettings.propertyNames.credentialsExpirationTimeInMs, value);
        this.credentialsExpirationTimeInMsCache = undefined;
    }

    public set updateRescheduled(value: boolean) {
        this.setProperty(CommonAdminSettings.propertyNames.updateRescheduled, value);
    }

    constructor(objectWrapper: PlainVersionedObject, handlers: VersionedObjectHandlers, gatewayService: GatewayConnection) {
        super(objectWrapper, handlers, gatewayService);
    }
    /**
     * Attempts to upgrade the current version of the object at least one version toward the latest version.
     * if this.currentVersion is null or undefined, then the upgrade should initialize to the latest version
     * this is called iteratively until the current version is equal to the latest version
     */
    protected upgrade() {
        // For now, we dont need to do anything but initialize to the latest version.
        // NOTE: When latestVersion is updated, then we need to add logic here to upgrade old admin settings objects.
        // In this case, we ALWAYS must maintain backwards and forwards compatibility. This object is shared back and forth be

        if (MsftSme.isNullOrUndefined(this.currentVersion)) {
            this.clear();
            this.currentVersion = this.latestVersion;
            this.connectivityLevel = '';
            this.scheduledUpdateTime = null;
            this.updateRescheduled = false;
            this.autoExtensionsUpdate = true;

            this.autoGatewayUpdate = false;
            this.gatewayService.get('gateway/autoupdate').subscribe((result: any) => this.autoGatewayUpdate = result.autoUpdate);

            return;
        } else if (this.currentVersion < this.latestVersion) {
            this.currentVersion = this.latestVersion;
        } else {
            // current version is newer than the version we know about. TODO: there may need to be handling here in the future.
        }
    }
}

/**
 * Defines the interface for the settings manager.
 */
export interface SettingsQuery {
    httpServerHeader: string;
    getCommonUserSettings(): Observable<CommonUserSettings>;
    getCommonApplicationSettings(): Observable<CommonApplicationSettings>;
    getCommonAdminSettings(): Observable<CommonAdminSettings>;
    getExtensionUserSettings<T extends VersionedObject>(type: VersionedObjectConstructor<T>): Observable<T>;
    getExtensionApplicationSettings<T extends VersionedObject>(type: VersionedObjectConstructor<T>): Observable<T>;
    getExtensionAdminSettings<T extends VersionedObject>(type: VersionedObjectConstructor<T>): Observable<T>;
}

/**
 * Manager for the settings. Provides an api for managing user and application settings.
 */
export class SettingsManager implements SettingsQuery {

    /**
     * Initializes a new instance of the SettingsManager class.
     *
     * @param rpc the RPC class instance.
     */
    constructor(private rpc: Rpc, private gatewayService: GatewayConnection) { }

    /**
     * Gets the http server header if any.
     */
    get httpServerHeader(): string {
        return null;
    }

    /**
     * Get common user settings. This is currently read-only
     */
    public getCommonUserSettings(): Observable<CommonUserSettings> {
        return from(RpcSettingsClient.settings(
            this.rpc,
            <RpcSettings>{
                operation: RpcSettingsOperationType.Get,
                scope: RpcSettingsScope.User,
                provider: RpcSettingsProviderType.Common
            })
        )
            .pipe(map(response => {
                // If the extension settings are not versioned (or not defined), then start with an empty settings.
                const settings = VersionedObject.ensureIsVersionedObject(response.data);
                return new CommonUserSettings(settings, {
                    save: (object) => {
                        return <Observable<any>>from(RpcSettingsClient.settings(
                            this.rpc,
                            <RpcSettings>{
                                operation: RpcSettingsOperationType.Set,
                                scope: RpcSettingsScope.User,
                                provider: RpcSettingsProviderType.Common,
                                value: object
                            }));
                    }
                });
            }));
    }

    /**
     * Get common application settings. This is currently read-only
     */
    public getCommonApplicationSettings(): Observable<CommonApplicationSettings> {
        return from(RpcSettingsClient.settings(
            this.rpc,
            <RpcSettings>{
                operation: RpcSettingsOperationType.Get,
                scope: RpcSettingsScope.Application,
                provider: RpcSettingsProviderType.Common
            })
        )
            .pipe(map(response => {
                // If the extension settings are not versioned (or not defined), then start with an empty settings.
                const settings = VersionedObject.ensureIsVersionedObject(response.data);
                return new CommonApplicationSettings(settings, {
                    save: (object) => {
                        return <Observable<any>>from(RpcSettingsClient.settings(
                            this.rpc,
                            <RpcSettings>{
                                operation: RpcSettingsOperationType.Set,
                                scope: RpcSettingsScope.Application,
                                provider: RpcSettingsProviderType.Common,
                                value: object
                            }));
                    }
                });
            }));
    }

    /**
     * Get common admin settings. This is currently read-only
     */
    public getCommonAdminSettings(): Observable<CommonAdminSettings> {
        return from(RpcSettingsClient.settings(
            this.rpc,
            <RpcSettings>{
                operation: RpcSettingsOperationType.Get,
                scope: RpcSettingsScope.Admin,
                provider: RpcSettingsProviderType.Common
            })
        )
            .pipe(map(response => {
                // If the extension settings are not versioned (or not defined), then start with an empty settings.
                const settings = VersionedObject.ensureIsVersionedObject(response.data);
                return new CommonAdminSettings(
                    settings,
                    {
                        save: (object) => {
                            return <Observable<any>>from(RpcSettingsClient.settings(
                                this.rpc,
                                <RpcSettings>{
                                    operation: RpcSettingsOperationType.Set,
                                    scope: RpcSettingsScope.Admin,
                                    provider: RpcSettingsProviderType.Common,
                                    value: object
                                }));
                        }
                    },
                    this.gatewayService);
            }));
    }

    /**
     * Get extension user settings
     * Extension settings objects must be an object that extends VersionedObject or implements the VersionedObjectConstructor
     * example: if TestObject extends VersionedObject, then getExtensionUserSettings(TestObject) will return an Observable<TestObject>
     * you should only create 1 versioned object for your extensions user settings.
     */
    public getExtensionUserSettings<T extends VersionedObject>(type: VersionedObjectConstructor<T>): Observable<T> {
        return from(RpcSettingsClient.settings(
            this.rpc,
            <RpcSettings>{
                operation: RpcSettingsOperationType.Get,
                scope: RpcSettingsScope.User,
                provider: RpcSettingsProviderType.Extension
            })
        )
            .pipe(map(response => {
                // If the extension settings are not versioned (or not defined), then start with an empty settings.
                const settings = VersionedObject.ensureIsVersionedObject(response.data);
                return new type(settings, {
                    save: (object) => {
                        return <Observable<any>>from(RpcSettingsClient.settings(
                            this.rpc,
                            <RpcSettings>{
                                operation: RpcSettingsOperationType.Set,
                                scope: RpcSettingsScope.User,
                                provider: RpcSettingsProviderType.Extension,
                                value: object
                            }));
                    }
                });
            }));
    }

    /**
     * Get extension application settings
     * Extension settings objects must be an object that extends VersionedObject or implements the VersionedObjectConstructor
     * example: if TestObject extends VersionedObject, then getExtensionApplicationSettings(TestObject) will return an
     * Observable<TestObject>. You should only create 1 versioned object for your extensions application settings.
     */
    public getExtensionApplicationSettings<T extends VersionedObject>(type: VersionedObjectConstructor<T>): Observable<T> {
        return from(RpcSettingsClient.settings(
            this.rpc,
            <RpcSettings>{
                operation: RpcSettingsOperationType.Get,
                scope: RpcSettingsScope.Application,
                provider: RpcSettingsProviderType.Extension
            })
        )
            .pipe(map(response => {
                // If the extension settings are not versioned (or not defined), then start with an empty settings.
                const settings = VersionedObject.ensureIsVersionedObject(response.data);
                return new type(settings, {
                    save: (object) => {
                        return <Observable<any>>from(RpcSettingsClient.settings(
                            this.rpc,
                            <RpcSettings>{
                                operation: RpcSettingsOperationType.Set,
                                scope: RpcSettingsScope.Application,
                                provider: RpcSettingsProviderType.Extension,
                                value: object
                            }));
                    }
                });
            }));
    }

    /**
     * Get extension admin settings
     * Extension settings objects must be an object that extends VersionedObject or implements the VersionedObjectConstructor
     * example: if TestObject extends VersionedObject, then getExtensionAdminSettings(TestObject) will return an
     * Observable<TestObject>. You should only create 1 versioned object for your extensions admin settings.
     */
    public getExtensionAdminSettings<T extends VersionedObject>(type: VersionedObjectConstructor<T>): Observable<T> {
        return from(RpcSettingsClient.settings(
            this.rpc,
            <RpcSettings>{
                operation: RpcSettingsOperationType.Get,
                scope: RpcSettingsScope.Admin,
                provider: RpcSettingsProviderType.Extension
            })
        )
            .pipe(map(response => {
                // If the extension settings are not versioned (or not defined), then start with an empty settings.
                const settings = VersionedObject.ensureIsVersionedObject(response.data);
                return new type(settings, {
                    save: (object) => {
                        return <Observable<any>>from(RpcSettingsClient.settings(
                            this.rpc,
                            <RpcSettings>{
                                operation: RpcSettingsOperationType.Set,
                                scope: RpcSettingsScope.Admin,
                                provider: RpcSettingsProviderType.Extension,
                                value: object
                            }));
                    }
                });
            }));
    }
}
